<script setup lang="ts">
import { twMerge } from 'tailwind-merge'
import type { IconDetail } from '~~/shared/types/menu'
import { ADDITIVE_GROUP_ID, buildAllergenTree } from '~/shared/allergenHierarchy'
import type { AllergenEntry, AllergenGroupEntry } from '~/shared/allergenHierarchy'

defineProps<{
  icons: IconDetail[]
}>()

const { styles } = useVariants('filterBar')
const { t } = useI18n()
const filter = useAllergenFilter()

const search = ref('')
const q = computed(() => search.value.trim().toLowerCase())

type Tab = 'allergens' | 'additives'
const activeTab = ref<Tab>('allergens')

// The scrollable list; reset to top on tab switch so you don't stay scrolled
// down into the new tab's content.
const listEl = ref<HTMLElement | null>(null)
watch(activeTab, () => {
  if (listEl.value) listEl.value.scrollTop = 0
})

// Merge the active modifier through twMerge so its border/color/weight win over
// the inactive base classes (plain class concatenation would let CSS order decide).
function tabClass(tab: Tab): string {
  return twMerge(styles.value.tab(), activeTab.value === tab ? styles.value.tabActive() : '')
}
const hasAdditives = computed(() => filter.additives.value.length > 0)
// Tabs only exist when there are additives and the user isn't searching;
// searching collapses everything into one combined result list.
const showTabs = computed(() => hasAdditives.value && !q.value)

// Reset to a clean state each time the modal opens.
watch(filter.isFilterOpen, (open) => {
  if (open) {
    search.value = ''
    activeTab.value = 'allergens'
  }
})

// Allergen master state — scoped to the allergen tab (additives excluded).
const isAllAllergensSelected = computed(() =>
  filter.nonAdditiveAllergens.value.length > 0
  && filter.nonAdditiveAllergens.value.every(i => filter.pendingExcludedAllergens.value.has(i.id)),
)
const isNoAllergensSelected = computed(() =>
  !filter.nonAdditiveAllergens.value.some(i => filter.pendingExcludedAllergens.value.has(i.id)),
)

// Trait master state (based on pending)
const isAllTraitsSelected = computed(() => filter.pendingTraits.value.size === filter.traits.value.length)
const isNoTraitsSelected = computed(() => filter.pendingTraits.value.size === 0)

function iconLabel(id: string): string {
  const key = `allergy.${id}`
  const val = t(key)
  return val === key ? id : val
}

function matches(id: string): boolean {
  return iconLabel(id).toLowerCase().includes(q.value)
}

const filteredTraits = computed(() => {
  if (!q.value) return filter.traits.value
  return filter.traits.value.filter(i => matches(i.id))
})

const fullTree = computed(() => buildAllergenTree(filter.allergens.value))

function isAdditiveGroup(entry: AllergenEntry): boolean {
  return entry.type === 'group' && entry.parentId === ADDITIVE_GROUP_ID
}

// The additive group and the remaining allergen entries, split for the tabs.
const additiveGroup = computed<AllergenGroupEntry | undefined>(() =>
  fullTree.value.find(e => isAdditiveGroup(e)) as AllergenGroupEntry | undefined,
)
const allergenEntries = computed(() => fullTree.value.filter(e => !isAdditiveGroup(e)))

function filterTree(tree: AllergenEntry[]): AllergenEntry[] {
  if (!q.value) return tree
  const out: AllergenEntry[] = []
  for (const entry of tree) {
    if (entry.type === 'single') {
      if (matches(entry.icon.id)) out.push(entry)
    }
    else if (matches(entry.parentId)) {
      out.push(entry)
    }
    else {
      const children = entry.children.filter(c => matches(c.id))
      if (children.length) out.push({ ...entry, children })
    }
  }
  return out
}

// Traits shown: filtered when searching, all on the allergen tab, none on the
// additives tab.
const traitList = computed(() => {
  if (q.value) return filteredTraits.value
  return activeTab.value === 'allergens' ? filter.traits.value : []
})

// Allergen entries shown: the full tree (incl. additives) when searching, the
// allergen entries on the allergen tab, the additive group on its tab.
const allergenList = computed<AllergenEntry[]>(() => {
  if (q.value) return filterTree(fullTree.value)
  if (activeTab.value === 'allergens') return allergenEntries.value
  return additiveGroup.value ? [additiveGroup.value] : []
})

// The traits section + allergen master belong to the allergen context
// (allergen tab, or the combined search view). Not to the additives tab.
const inAllergenContext = computed(() => !!q.value || activeTab.value === 'allergens')
const showAllergenChrome = computed(() => inAllergenContext.value && !q.value)

// Section title + description: per active tab, but "Allergenen" in the combined
// search view (where both are shown at once).
const sectionLabel = computed(() =>
  !q.value && activeTab.value === 'additives' ? t('filterTabAdditives') : t('filterAllergenen'),
)
const sectionInfo = computed(() =>
  activeTab.value === 'additives' ? t('filterAdditiveInfo') : t('filterAllergenInfo'),
)

const hasResults = computed(() => traitList.value.length > 0 || allergenList.value.length > 0)

function entryKey(entry: AllergenEntry): string {
  return entry.type === 'group' ? `g-${entry.parentId}` : `s-${entry.icon.id}`
}

function close() {
  filter.discardFilter()
}

function apply() {
  filter.applyFilter()
}

function reset() {
  filter.resetFilter()
  search.value = ''
}

function onAllergenMasterClick() {
  if (isAllAllergensSelected.value) filter.deselectAllAllergens()
  else filter.selectAllAllergens()
}

function onTraitMasterClick() {
  if (isAllTraitsSelected.value) filter.deselectAllTraits()
  else filter.selectAllTraits()
}
</script>

<template>
  <!-- Trigger bar -->
  <button
    :class="styles.bar()"
    :aria-expanded="filter.isFilterOpen.value"
    :aria-label="$t('filterTitle')"
    @click="filter.openFilter()"
  >
    <div :class="styles.barContainer()">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="h-4 w-4 shrink-0"
      >
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
      </svg>
      <span :class="styles.barTitle()">{{ $t('filterTitle') }}</span>
    </div>
  </button>

  <!-- Popover via Teleport -->
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-150"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="filter.isFilterOpen.value">
        <!-- Backdrop -->
        <div
          :class="styles.backdrop()"
          @click="close"
        />

        <!-- Popover panel -->
        <div :class="styles.popover()">
          <!-- Header -->
          <div :class="styles.popoverHeader()">
            <span :class="styles.popoverTitle()">{{ $t('filterTitle') }}</span>
            <button
              :class="styles.closeButton()"
              :aria-label="$t('back')"
              @click="close"
            >
              <svg :class="styles.closeButtonInner()" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path fill="currentColor" d="M183.1 137.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L275.2 320L137.9 457.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l137.3-137.4l137.4 137.3c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L365.8 320l137.3-137.4c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L320.5 274.7z" /></svg>
            </button>
          </div>

          <!-- List -->
          <div ref="listEl" :class="styles.list()">
            <!-- Sticky header: search + tabs stay pinned together (no gap) -->
            <div :class="styles.stickyHeader()">
              <!-- Search (always visible, searches across every tab) -->
              <div :class="styles.search()">
                <input
                  v-model="search"
                  type="search"
                  :placeholder="$t('filterSearch')"
                  :class="styles.searchInput()"
                >
              </div>

              <!-- Tabs (hidden while searching, which shows a combined list) -->
              <div v-if="showTabs" :class="styles.tabList()">
                <button
                  type="button"
                  :class="tabClass('allergens')"
                  :aria-pressed="activeTab === 'allergens'"
                  @click="activeTab = 'allergens'"
                >
                  {{ $t('filterTabAllergens') }}
                </button>
                <button
                  type="button"
                  :class="tabClass('additives')"
                  :aria-pressed="activeTab === 'additives'"
                  @click="activeTab = 'additives'"
                >
                  {{ $t('filterTabAdditives') }}
                </button>
              </div>
            </div>

            <!-- Traits section (allergen tab / combined search only) -->
            <template v-if="traitList.length">
              <div :class="styles.groupLabel()">
                {{ $t('filterTraits') }}
              </div>

              <!-- Trait master checkbox -->
              <div
                v-if="showAllergenChrome"
                :class="styles.masterItem()"
                @click="onTraitMasterClick"
              >
                <FilterCheckbox
                  :checked="isAllTraitsSelected"
                  :indeterminate="!isAllTraitsSelected && !isNoTraitsSelected"
                  :class="styles.checkbox()"
                />
                <span :class="styles.itemLabel()">{{ $t('filterSelectAllTraits') }}</span>
              </div>

              <!-- Trait items -->
              <div
                v-for="icon in traitList"
                :key="icon.id"
                :class="styles.listItem()"
                @click="filter.toggleTrait(icon.id)"
              >
                <FilterCheckbox
                  :checked="filter.pendingTraits.value.has(icon.id)"
                  :class="styles.checkbox()"
                />
                <MenuIcon :icon="icon.id" />
                <span :class="styles.itemLabel()">{{ iconLabel(icon.id) }}</span>
              </div>

              <div :class="styles.groupSeparator()" />
            </template>

            <!-- Allergen / additive entries -->
            <template v-if="allergenList.length">
              <!-- Section title: 'Allergenen' / 'Additieven' per tab -->
              <div :class="styles.groupLabel()">
                {{ sectionLabel }}
              </div>

              <!-- Section description (hidden while searching) -->
              <p v-if="!q" :class="styles.allergenInfo()">
                {{ sectionInfo }}
              </p>

              <!-- Allergen master checkbox -->
              <div
                v-if="showAllergenChrome"
                :class="styles.masterItem()"
                @click="onAllergenMasterClick"
              >
                <FilterCheckbox
                  :checked="isAllAllergensSelected"
                  :indeterminate="!isAllAllergensSelected && !isNoAllergensSelected"
                  :class="styles.checkbox()"
                />
                <span :class="styles.itemLabel()">{{ $t('filterSelectAllAllergens') }}</span>
              </div>

              <!-- Allergen entries (groups + singles) -->
              <template v-for="entry in allergenList" :key="entryKey(entry)">
                <!-- Group: parent (select-all) + nested subs -->
                <template v-if="entry.type === 'group'">
                  <div
                    :class="styles.masterItem()"
                    @click="filter.toggleAllergen(entry.parentId)"
                  >
                    <FilterCheckbox
                      :checked="filter.allergenGroupState(entry.parentId) === 'checked'"
                      :indeterminate="filter.allergenGroupState(entry.parentId) === 'indeterminate'"
                      :class="styles.checkbox()"
                    />
                    <MenuIcon :icon="entry.parentId" />
                    <span :class="styles.itemLabel()">{{ iconLabel(entry.parentId) }}</span>
                  </div>
                  <div
                    v-for="child in entry.children"
                    :key="child.id"
                    :class="styles.subItem()"
                    @click="filter.toggleAllergen(child.id)"
                  >
                    <FilterCheckbox
                      :checked="filter.pendingExcludedAllergens.value.has(child.id)"
                      :class="styles.checkbox()"
                    />
                    <MenuIcon :icon="child.id" />
                    <span :class="styles.itemLabel()">{{ iconLabel(child.id) }}</span>
                  </div>
                </template>

                <!-- Single allergen -->
                <div
                  v-else
                  :class="styles.listItem()"
                  @click="filter.toggleAllergen(entry.icon.id)"
                >
                  <FilterCheckbox
                    :checked="filter.pendingExcludedAllergens.value.has(entry.icon.id)"
                    :class="styles.checkbox()"
                  />
                  <MenuIcon :icon="entry.icon.id" />
                  <span :class="styles.itemLabel()">{{ iconLabel(entry.icon.id) }}</span>
                </div>
              </template>
            </template>

            <!-- Empty search state -->
            <div v-if="q && !hasResults" :class="styles.searchEmpty()">
              {{ $t('filterSearchNoResults') }}
            </div>
          </div>

          <!-- Footer -->
          <div :class="styles.popoverFooter()">
            <button
              :class="styles.resetButton()"
              @click="reset"
            >
              {{ $t('filterReset') }}
            </button>
            <button
              :class="styles.applyButton()"
              @click="apply"
            >
              {{ $t('filterApply') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
